<template>
  <inline-date-editor
    ref="editorRef"
    :id="editorId"
    v-model="theModel"
    :editable="editable"
    :compact="isTable"
    :move-to-body="true"
    @commit="onCommit"
  />
</template>
<script lang="ts">
import { Component, Prop, Vue } from "vue-facing-decorator";
import {
  ParameterInstance,
} from "../../../models/interfaces/parameters/parameter-models";
import InlineDateEditor from "../../ui-kit/datepicker/InlineDateEditor.vue";

/**
 * A component for displaying DateValue type. Adapter between a parameter instance and
 * InlineDateEditor. Turns DateValue into Date and back, raises update nested with an ISO
 * string so that root runs the persist, and forwards to the editor the two calls the
 * descriptor makes to a live field, closing it and releasing the caret.
 */

@Component({
  name: "ParameterDateInline",
  components: { InlineDateEditor },
  inheritAttrs: false,
  emits: ["update:modelValue", "update:nested"],
  expose: [
    "leaveField",
    "releaseFieldFocus",
  ],
})
export default class ParameterDateInline extends Vue {
  @Prop({ default: {} as ParameterInstance })
  modelValue!: ParameterInstance;
  @Prop({ type: Boolean, default: false }) editable!: boolean;
  @Prop({ default: undefined }) nestedIndex!: number | undefined;
  @Prop({ type: String, default: "" }) id!: string;
  // Table mode of the descriptor means 12px text, and the editor scales its icons to it
  @Prop({ type: Boolean, default: false }) isTable!: boolean;

  get editorId(): string {
    return this.id || `param-date-${this.modelValue?.Id ?? 0}`;
  }

  // The chosen day lives in the UTC parts of DateValue, so it is read from them and
  // written back into them. Taking the local day instead would move the date by one day
  // in every zone east or west of UTC once the value goes to the server as ISO
  get theModel(): Date | null {
    const raw = this.modelValue.DateValue;
    if (!raw) {
      return null;
    }
    const stored = new Date(raw);
    if (Number.isNaN(stored.getTime())) {
      return null;
    }
    return new Date(
      stored.getUTCFullYear(),
      stored.getUTCMonth(),
      stored.getUTCDate(),
      0,
      0,
      0,
      0
    );
  }

  set theModel(newValue: Date | null) {
    this.modelValue.DateValue = newValue ? this.toStoredValue(newValue) : null;
    this.$emit('update:modelValue', this.modelValue)
  }

  // Time of day of a stored value is kept as it is, otherwise the ISO string in the
  // request changes where the user touched nothing but the day
  private toStoredValue(value: Date): Date {
    const previous = this.modelValue.DateValue
      ? new Date(this.modelValue.DateValue)
      : null;
    const known = !!previous && !Number.isNaN(previous.getTime());
    return new Date(
      Date.UTC(
        value.getFullYear(),
        value.getMonth(),
        value.getDate(),
        known ? previous!.getUTCHours() : 0,
        known ? previous!.getUTCMinutes() : 0,
        known ? previous!.getUTCSeconds() : 0,
        known ? previous!.getUTCMilliseconds() : 0
      )
    );
  }

  // The value is already in DateValue, the v-model setter runs before commit
  onCommit() {
    const stored = this.modelValue.DateValue
      ? new Date(this.modelValue.DateValue)
      : null;
    // The same pipeline as the checkbox has, the event goes up to root and root runs
    // the persist. eventKind 2 means a changed value, and the fresh date goes into
    // sectionText as ISO, which root takes as the source of truth
    this.$emit('update:nested', {
      fieldKey: null,
      sectionText: stored ? stored.toISOString() : '',
      eventKind: 2,
      nestedIndex: this.nestedIndex,
      opts: {
        InstanceId: this.modelValue.Id,
        ValueType: this.modelValue.ValueType,
        ValueSet: this.modelValue.ValueSet,
        TemplateId: this.modelValue.TemplateId,
        selectedId: this.modelValue.SelectedValue?.Id,
      },
    })
  }

  // The descriptor closes a live field and takes the caret out of it by walking its
  // children, so both calls are forwarded to the editor
  leaveField(): boolean {
    const editor = this.$refs.editorRef as any;
    return !!editor?.leaveField?.();
  }

  releaseFieldFocus(): void {
    const editor = this.$refs.editorRef as any;
    editor?.releaseFieldFocus?.();
  }
}
</script>
